/**
 * OverviewTab - Overview metrics tab content
 *
 * Displays time period filters, risk summary cards (High Risk, Critical),
 * aggregate metric cards (Total Customers, Average Balance, etc.),
 * and analytics section with risk indicator distribution charts.
 */

import React from 'react';
import { OverviewTabProps, formatINR, formatIndianNumber } from '../../../types/ews.types';
import { EWSRiskCard } from './EWSRiskCard';
import { EWSMetricCard } from './EWSMetricCard';
import { EWSAnalyticsSection } from './EWSAnalyticsSection';

// Import EWS icons
import ewsTotalCustomersIcon from '../../../assets/images/ews_total_customers_card_icon.png';
import ewsAverageBalanceIcon from '../../../assets/images/ews_average_balance_card_icon.png';
import ewsEmiIcon from '../../../assets/images/ews_emi_card_icon.png';
import ewsSipIcon from '../../../assets/images/ews_sip_card_icon.png';

/**
 * Loading skeleton for metric cards.
 */
const MetricCardSkeleton: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-24 mb-3" />
    <div className="h-8 bg-gray-200 rounded w-20" />
  </div>
);

/**
 * Loading skeleton for risk cards.
 */
const RiskCardSkeleton: React.FC = () => (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-20 mb-3" />
    <div className="h-10 bg-gray-200 rounded w-24 mb-2" />
    <div className="h-4 bg-gray-200 rounded w-32" />
  </div>
);

export const OverviewTab: React.FC<OverviewTabProps> = ({
  metrics,
  isLoading,
  distributions,
  isLoadingDistributions,
  selectedRiskFilter,
  onRiskFilterChange,
  onBarClick,
}) => {
  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        {/* Risk Summary Cards */}
        <div className="grid grid-cols-2 gap-1.5">
          {isLoading ? (
            <>
              <RiskCardSkeleton />
              <RiskCardSkeleton />
            </>
          ) : metrics ? (
            <>
              <EWSRiskCard
                title="High Risk"
                count={metrics.highRiskCount}
                percentage={metrics.highRiskPercentage}
                variant="high"
                onClick={() => console.log('Navigate to high risk customers')}
                infoTooltip="Customers with extreme risk alerts indicating urgent action required."
              />
              <EWSRiskCard
                title="Critical"
                count={metrics.criticalCount}
                percentage={metrics.criticalPercentage}
                variant="critical"
                onClick={() => console.log('Navigate to critical customers')}
                infoTooltip="Customers with early-stage risk alerts that may escalate to High Risk."
              />
            </>
          ) : null}
        </div>

        {/* Aggregate Metric Cards */}
        <div className="grid grid-cols-4 gap-1.5">
          {isLoading ? (
            <>
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </>
          ) : metrics ? (
            <>
              <EWSMetricCard
                title="Total Customers"
                value={formatIndianNumber(metrics.totalCustomers)}
                backgroundImage={ewsTotalCustomersIcon}
                infoTooltip="Total number of customers being monitored."
              />
              <EWSMetricCard
                title="Average Balance"
                value={formatINR(metrics.averageBalance)}
                backgroundImage={ewsAverageBalanceIcon}
                infoTooltip="Average bank balance across monitored customers."
              />
              <EWSMetricCard
                title="Average EMI Count"
                value={metrics.averageEMICount.toFixed(1)}
                backgroundImage={ewsEmiIcon}
                infoTooltip="Average number of active EMIs per customer."
              />
              <EWSMetricCard
                title="Average SIPs"
                value={metrics.averageSIPs.toFixed(1)}
                backgroundImage={ewsSipIcon}
                infoTooltip="Average number of active SIPs per customer."
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Analytics Section - Risk Indicator Distribution Charts */}
      <EWSAnalyticsSection
        distributions={distributions}
        isLoading={isLoadingDistributions}
        selectedRiskFilter={selectedRiskFilter}
        onRiskFilterChange={onRiskFilterChange}
        onBarClick={onBarClick}
      />
    </div>
  );
};

export default OverviewTab;
